import { useState } from 'react'
import { V2ProductCatalogSupplierDialog } from '@/v2/components/V2ProductCatalogSupplierDialog'
import { getV2SupplierDetailPath, V2_SUPPLIERS_PATH } from '@/v2/utils/routes'
import { Factory, Loader2, Pencil, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useGetProductCatalogSupplier } from '@/shared/hooks/api/catalog'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
} from '@/components/ui/empty'

type V2ProductCatalogSupplierCardProps = {
  productId: string
}

function formatSupplierContact(
  contactName: string | null,
  contactEmail: string | null
): string | null {
  const parts = [contactName, contactEmail]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
  return parts.length > 0 ? parts.join(' | ') : null
}

export function V2ProductCatalogSupplierCard({
  productId,
}: V2ProductCatalogSupplierCardProps) {
  const { t } = useTranslation('common')
  const [dialogOpen, setDialogOpen] = useState(false)
  const { data, isLoading, isError } = useGetProductCatalogSupplier(productId)
  const supplier = data?.catalogSupplier ?? null
  const contact = supplier
    ? formatSupplierContact(supplier.contactName, supplier.contactEmail)
    : null

  return (
    <>
      <Card className='h-fit'>
        <CardHeader className='space-y-1'>
          <CardTitle className='text-base leading-tight'>
            {t('v2.pages.products.detail.productSupplier.title')}
          </CardTitle>
          <CardDescription className='text-xs'>
            {t('v2.pages.products.detail.productSupplier.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='flex items-center justify-center py-6'>
              <Loader2 className='size-6 animate-spin text-muted-foreground' />
            </div>
          ) : isError ? (
            <p className='text-sm text-muted-foreground'>
              {t('v2.pages.products.detail.productSupplier.loadError')}
            </p>
          ) : supplier ? (
            <div className='flex items-start gap-2 rounded-lg border bg-background p-3 transition-colors hover:bg-muted/50'>
              <Link
                aria-label={t(
                  'v2.pages.products.detail.productSupplier.viewSupplierAriaLabel',
                  { name: supplier.name }
                )}
                className='group flex min-w-0 flex-1 items-start gap-3'
                to={getV2SupplierDetailPath(supplier.id)}
              >
                <Factory className='mt-0.5 size-5 shrink-0 text-primary' />
                <div className='min-w-0 space-y-1'>
                  <p className='truncate text-sm font-medium text-primary underline-offset-4 group-hover:underline'>
                    {supplier.name}
                  </p>
                  {contact ? (
                    <CardDescription className='text-xs'>
                      {contact}
                    </CardDescription>
                  ) : null}
                </div>
              </Link>
              <Button
                aria-label={t(
                  'v2.pages.products.detail.productSupplier.editAriaLabel'
                )}
                className='size-8 shrink-0 text-muted-foreground hover:text-foreground'
                onClick={() => {
                  setDialogOpen(true)
                }}
                size='icon'
                type='button'
                variant='ghost'
              >
                <Pencil className='size-4' aria-hidden />
              </Button>
            </div>
          ) : (
            <Empty className='border-0 px-0 py-2'>
              <EmptyHeader>
                <EmptyDescription>
                  {t('v2.pages.products.detail.productSupplier.empty')}
                </EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <Button
                  onClick={() => {
                    setDialogOpen(true)
                  }}
                  size='sm'
                >
                  <Plus className='size-4' aria-hidden />
                  {t('v2.pages.products.detail.productSupplier.select')}
                </Button>
                <p className='text-xs text-muted-foreground'>
                  {t(
                    'v2.pages.products.detail.productSupplier.noSuppliersHint'
                  )}{' '}
                  <Link
                    className='font-medium text-primary underline-offset-4 hover:underline'
                    to={V2_SUPPLIERS_PATH}
                  >
                    {t(
                      'v2.pages.products.detail.productSupplier.addSuppliersLink'
                    )}
                  </Link>
                </p>
              </EmptyContent>
            </Empty>
          )}
        </CardContent>
      </Card>
      <V2ProductCatalogSupplierDialog
        currentSupplier={supplier}
        onOpenChange={setDialogOpen}
        open={dialogOpen}
        productId={productId}
      />
    </>
  )
}
